import React, { useEffect, useState } from "react";
import { Alert, Col, Input, Row, Space, Typography } from "antd";
import { EnvironmentOutlined, HomeOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { Text } = Typography;

export interface ShippingAddress {
  formatted_address: string;
  street_address: string;
  locality?: string;
  administrative_area?: string;
  postal_code?: string;
  country?: string;
  additional_info?: string;
  landmark?: string;
  delivery_instructions?: string;
}

interface LocationPickerProps {
  onLocationSelect: (location: ShippingAddress) => void;
  initialValue?: Partial<ShippingAddress>;
}

const LocationPicker: React.FC<LocationPickerProps> = ({
  onLocationSelect,
  initialValue,
}) => {
  const [streetAddress, setStreetAddress] = useState(
    initialValue?.street_address || initialValue?.formatted_address || ""
  );
  const [additionalInfo, setAdditionalInfo] = useState(
    initialValue?.additional_info || ""
  );
  const [landmark, setLandmark] = useState(initialValue?.landmark || "");
  const [deliveryInstructions, setDeliveryInstructions] = useState(
    initialValue?.delivery_instructions || ""
  );

  useEffect(() => {
    onLocationSelect({
      formatted_address: streetAddress.trim(),
      street_address: streetAddress.trim(),
      additional_info: additionalInfo.trim(),
      landmark: landmark.trim(),
      delivery_instructions: deliveryInstructions.trim(),
    });
  }, [
    streetAddress,
    additionalInfo,
    landmark,
    deliveryInstructions,
    onLocationSelect,
  ]);

  return (
    <div>
      <Alert
        message={
          <Space>
            <EnvironmentOutlined />
            <Text>
              Indiquez librement le quartier, le secteur, la rue et tout détail
              utile au livreur.
            </Text>
          </Space>
        }
        type="info"
        showIcon={false}
        style={{ marginBottom: "1rem", fontSize: 13 }}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <div>
            <Text strong>Adresse détaillée *</Text>
          </div>
          <TextArea
            value={streetAddress}
            onChange={(event) => setStreetAddress(event.target.value)}
            placeholder="Ex. : quartier, secteur, rue, porte, commune ou localité"
            rows={3}
            size="large"
            style={{ marginTop: "0.25rem" }}
          />
        </Col>

        <Col xs={24}>
          <Text strong>Complément d&apos;adresse (optionnel)</Text>
          <Input
            value={additionalInfo}
            onChange={(event) => setAdditionalInfo(event.target.value)}
            placeholder="Étage, bâtiment, résidence, code d'accès..."
            prefix={<HomeOutlined />}
            size="large"
            style={{ marginTop: "0.25rem" }}
          />
        </Col>

        <Col xs={24}>
          <Text strong>Point de repère (optionnel)</Text>
          <Input
            value={landmark}
            onChange={(event) => setLandmark(event.target.value)}
            placeholder="Ex. : près de la station, en face du marché..."
            size="large"
            style={{ marginTop: "0.25rem" }}
          />
        </Col>

        <Col xs={24}>
          <Text strong>Instructions de livraison (optionnel)</Text>
          <TextArea
            value={deliveryInstructions}
            onChange={(event) => setDeliveryInstructions(event.target.value)}
            placeholder="Ex. : appeler 10 minutes avant l'arrivée..."
            rows={2}
            size="large"
            style={{ marginTop: "0.25rem" }}
          />
        </Col>
      </Row>

      {streetAddress.trim() && (
        <Alert
          message="Aperçu de votre adresse"
          description={
            <div>
              <Text strong>{streetAddress.trim()}</Text>
              {additionalInfo.trim() && (
                <>
                  <br />
                  <Text>{additionalInfo.trim()}</Text>
                </>
              )}
              {landmark.trim() && (
                <>
                  <br />
                  <Text type="secondary">
                    Point de repère : {landmark.trim()}
                  </Text>
                </>
              )}
            </div>
          }
          type="success"
          showIcon
          style={{ marginTop: "1rem" }}
        />
      )}
    </div>
  );
};

export default LocationPicker;
